import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { hostNameBack, today } from '../../utils/constants';
import { formatDate } from '../../utils/utilFuncs';

const GenerateAccountStatement = ({ sellerId }) => {
    const [showModal, setShowModal] = useState(false);
    const [filter, setFilter] = useState("1m");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [history, setHistory] = useState([]);
    const [seller, setSeller] = useState(null);
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem("adminToken");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [sellerResponse, historyResponse] = await Promise.all([
                    axios.get(`${hostNameBack}/api/v1/sellers/seller/${sellerId}`, {
                        headers: { Authorization: `Admin ${token}` }
                    }),
                    axios.get(`${hostNameBack}/api/v1/payments/seller/${sellerId}`, {
                        headers: { Authorization: `Admin ${token}` }
                    })
                ]);
                if (sellerResponse.data.success) {
                    setSeller(sellerResponse.data.seller);
                }
                if (historyResponse.data.success) {
                    setHistory(historyResponse.data.history);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };
        if (token && sellerId) {
            fetchData();
        }
    }, [sellerId, token]);

    const handleGeneratePdf = () => {
        if (filter === "custom" && (!customStartDate || !customEndDate)) {
            enqueueSnackbar("Please select custom dates", { variant: "error" });
            return;
        }

        if (!seller) {
            enqueueSnackbar("Seller data not available", { variant: "error" });
            return;
        }

        const doc = new jsPDF();
        doc.setFontSize(16);

        let rangeLabel;
        if (filter === "1m") rangeLabel = "Last 1 month";
        else if (filter === "3m") rangeLabel = "Last 3 months";
        else if (filter === "6m") rangeLabel = "Last 6 months";
        else if (filter === "1y") rangeLabel = "Last 1 year";
        else if (filter === "custom") rangeLabel = `From ${customStartDate} to ${customEndDate}`;
        else rangeLabel = "All Time";

        doc.text(`Account Statement - ${rangeLabel}`, 14, 20);
        doc.setFontSize(12);
        doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 30);

        doc.setFontSize(14);
        doc.text(`Seller: ${seller.fullName}`, 14, 40);
        let yPosition = 50;
        if (seller.companyName) {
            doc.text(`Company: ${seller.companyName}`, 14, yPosition);
            yPosition += 10;
        }
        if (seller.userId.address) {
            doc.text(`Address: ${seller.userId.address || ''}, ${seller.userId.city || ''}, ${seller.userId.zipCode || ''}`, 14, yPosition);
            yPosition += 10;
        }
        if (seller.userId.phoneNumber) {
            doc.text(`Phone: ${seller.userId.phoneNumber || ''}`, 14, yPosition);
            yPosition += 10;
        }

        let startDate, endDate = new Date();
        if (filter === "custom") {
            startDate = new Date(customStartDate);
            endDate = new Date(customEndDate);
            endDate.setHours(23, 59, 59, 999);
        } else {
            let rangeDays;
            switch (filter) {
                case "1m": rangeDays = 30; break;
                case "3m": rangeDays = 90; break;
                case "6m": rangeDays = 180; break;
                case "1y": rangeDays = 365; break;
                default: rangeDays = 0;
            }
            if (rangeDays > 0) {
                startDate = new Date();
                startDate.setDate(startDate.getDate() - rangeDays);
            } else {
                startDate = new Date(0);
            }
        }

        const openingBalance = history
            .filter(h => new Date(h.date) < startDate && (h.description === "Order Completed" || h.description === "Requested for Withdrawal"))
            .reduce((acc, h) => {
                if (h.description === "Order Completed") return acc + h.amount;
                else if (h.description === "Requested for Withdrawal") return acc - h.amount;
                return acc;
            }, 0);

        const transactions = history
            .filter(h => new Date(h.date) >= startDate && new Date(h.date) <= endDate && (h.description === "Order Completed" || h.description === "Requested for Withdrawal"))
            .sort((a, b) => new Date(a.date) - new Date(b.date));

        const totalEarnings = transactions
            .filter(t => t.description === "Order Completed")
            .reduce((sum, t) => sum + t.amount, 0);
        const totalWithdrawals = transactions
            .filter(t => t.description === "Requested for Withdrawal")
            .reduce((sum, t) => sum + t.amount, 0);

        const closingBalance = openingBalance + totalEarnings - totalWithdrawals;

        let balance = openingBalance;
        const transactionData = transactions.map(t => {
            const earnings = t.description === "Order Completed" ? t.amount : 0;
            const withdrawals = t.description === "Requested for Withdrawal" ? t.amount : 0;
            const buyer = t.description === "Order Completed" ? (t.buyerUsername || '-') : '-';
            const paymentMethod = t.paymentMethod || '-';
            if (t.description === "Order Completed") balance += t.amount;
            else if (t.description === "Requested for Withdrawal") balance -= t.amount;
            return [formatDate(t.date), t.description, buyer, paymentMethod, earnings ? `$${earnings.toFixed(2)}` : '', withdrawals ? `$${withdrawals.toFixed(2)}` : '', `$${balance.toFixed(2)}`];
        });

        const summaryData = [
            ['Opening Balance', `$${openingBalance.toFixed(2)}`],
            ['Total Earnings', `$${totalEarnings.toFixed(2)}`],
            ['Total Withdrawals', `$${totalWithdrawals.toFixed(2)}`],
            ['Closing Balance', `$${closingBalance.toFixed(2)}`]
        ];

        autoTable(doc, {
            startY: yPosition,
            head: [['Summary Item', 'Amount']],
            body: summaryData,
            theme: 'grid',
            styles: { fontSize: 12 },
            columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 60 } },
        });

        doc.setFontSize(14);
        doc.text('Transaction History', 14, doc.lastAutoTable.finalY + 10);
        if (transactionData.length === 0) {
            doc.setFontSize(12);
            doc.text('No transactions in this period.', 14, doc.lastAutoTable.finalY + 20);
        } else {
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY + 20,
                head: [['Date', 'Description', 'Buyer', 'Payment Method', 'Earnings', 'Withdrawals', 'Balance']],
                body: transactionData,
                didParseCell: function (data) {
                    if (data.section === 'body') {
                        if (data.column.index === 4 && data.cell.text[0] !== '') {
                            data.cell.styles.textColor = [0, 128, 0];
                        } else if (data.column.index === 5 && data.cell.text[0] !== '') {
                            data.cell.styles.textColor = [255, 0, 0];
                        }
                    }
                },
            });
        }

        doc.save("account-statement.pdf");
    };

    return (
        <>
            <button
                className="secondaryBtn generateReportBtn"
                onClick={() => setShowModal(true)}
                disabled={loading || !sellerId}
            >
                Generate Account Statement
            </button>
            {showModal && (
                <div className="popupDiv">
                    <div className="popupContent" style={{alignItems: "center"}}>
                        <div className="form">
                            <h2 className='secondaryHeading'>Select <span>Time Period</span> for Account Statement</h2>
                            <div className="horizontalLine"></div>
                            <select
                                onChange={e => setFilter(e.target.value)}
                                value={filter}
                                className="dropdownPlus"
                            >
                                <option value="1m">Last 1 month</option>
                                <option value="3m">Last 3 months</option>
                                <option value="6m">Last 6 months</option>
                                <option value="1y">Last 1 year</option>
                                <option value="custom">Custom</option>
                            </select>
                            {filter === "custom" && (
                                <div className="customDateRange">
                                    <label>Start Date:</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        onChange={e => setCustomStartDate(e.target.value)}
                                        max={today}
                                    />
                                    <label>End Date:</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        onChange={e => setCustomEndDate(e.target.value)}
                                        max={today}
                                    />
                                </div>
                            )}
                            <div className="buttonsDiv">
                                <button
                                    className="primaryBtn"
                                    onClick={() => {
                                        handleGeneratePdf();
                                        setShowModal(false);
                                    }}
                                    disabled={loading}
                                >
                                    Generate
                                </button>
                                <button
                                    className="secondaryBtn"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default GenerateAccountStatement;